import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useCallback } from "react";
import SmartDPSearchForm from "app/pages/SmartDP/Search/SDPSearchForm/SmartDPSearchForm";
import PropTypes from "prop-types";
import { getTopDestinations } from "app/reducers/shopSelector";
import {
	QUOTATION_CODE_STATUS,
	RESOLUTION,
	SDP_PRODUCTS_FILTERS_KEY,
	WEBSITE_TUNNEL_TYPES,
} from "app/constants";
import {
	resetAllSDPProductsFilter,
	unsetSDPProductsFilter,
} from "app/pages/SmartDP/Listing/SmartDPFilters/smartDPProductsFilterActionCreators";
import { injectIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import {
	buildSDPSearchPayload,
	getLastSearchData,
	getTravelLocationDataFromQuery,
	hasCustomBookingOccupancies,
	normalizeDate,
	pickInitialValue,
} from "app/utils/smartDPUtils";
import withRouter from "app/utils/hocs/withRouter";
import { parse } from "qs";
import { isEmpty, isNil } from "lodash";

const SmartDPSearchFormContainer = props => {
	const { unsetSDPProductsFilter, onSubmit } = props;

	const { pathname } = useLocation();

	const isListingPage = pathname.includes("/listing");

	const navigate = useNavigate();

	const handleFormSubmission = useCallback(({ values, pathname = "" }) => {
		const queryParams = buildSDPSearchPayload(values);
		unsetSDPProductsFilter(SDP_PRODUCTS_FILTERS_KEY.BUDGET);

		if (onSubmit) {
			onSubmit(values);
		}

		if (pathname.includes("/sdp/listing")) {
			navigate({ pathname: pathname, search: queryParams }, { replace: true });
		} else {
			navigate({ pathname: "/sdp/listing", search: queryParams });
		}
	}, []);

	return (
		<div className="search-form__container">
			<SmartDPSearchForm
				{...props}
				onSuccess={handleFormSubmission}
				usePortalForMenu={isListingPage}
			/>
		</div>
	);
};

SmartDPSearchFormContainer.propTypes = {
	unsetSDPProductsFilter: PropTypes.func,
	isMobile: PropTypes.bool,
	brand: PropTypes.string,
	disableFormPersistance: PropTypes.bool, // utilisé pour éviter le flicker des champs entre la page merch et la home SDP
	onSubmit: PropTypes.func,
};

const mapStateToProps = (state, props) => {
	const queryParams = parse(props?.location?.search, { ignoreQueryPrefix: true });
	const { booking, smartDP } = state;
	const { disableFormPersistance } = props;
	const isMerchPage = props?.location?.pathname?.includes("/merch");
	const lastSearch = getLastSearchData(state, WEBSITE_TUNNEL_TYPES.HOLIDAYS);

	const initialValues = {
		travelDates: {
			departureDate: "",
			endDate: "",
		},
	};

	if (smartDP?.cabins?.[0]) {
		initialValues.cabin = smartDP.cabins[0];
	}

	const getLocationsFrom = query =>
		getTravelLocationDataFromQuery({
			queryParams: query,
			destinations: smartDP?.destinations,
			departures: smartDP?.departureCities,
		});

	const { departure: queryDeparture, destination: queryDestination } = getLocationsFrom({
		departureCityCode: queryParams?.departureCityCode,
		destinationResortCode: queryParams?.destinationResortCode,
		destinationIATA: queryParams?.destinationIATA,
	});

	const { departure: bookingDeparture, destination: bookingDestination } = getLocationsFrom({
		departureCityCode: booking?.departureCity?.code,
		destinationResortCode: booking?.destinationResort?.code,
	});

	const { departure: lastDeparture, destination: lastDestination } = getLocationsFrom({
		departureCityCode: lastSearch?.departureCity?.code,
		destinationResortCode: lastSearch?.destinationResort?.code,
	});

	// En merch, on ne va pas set  destination/dates mais on peut garder les autres champs
	const canUseLastSearchForMainFields = !disableFormPersistance && !isMerchPage;
	const canUseLastSearchForOtherFields = !disableFormPersistance;

	//  un helper pour ne pas répéter disableFormPersistance et garder la priorité booking -> requête -> dernière recherche.
	const pickValue = params =>
		pickInitialValue({
			disableFormPersistance,
			...params,
		});

	const normalizeValue = date => (date ? normalizeDate(date) : undefined);

	// 1. On privilégie la destination persistée, puis la query, puis la dernière recherche.
	const destinationResort = pickValue({
		bookingValue: bookingDestination,
		queryValue: queryDestination,
		lastSearchValue: lastDestination,
		allowLastSearch: canUseLastSearchForMainFields,
		isValueValid: value => !isEmpty(value),
	});

	const hasDestination = !isEmpty(destinationResort);

	if (hasDestination) {
		initialValues.destinationResort = destinationResort;

		const departureCity = pickValue({
			bookingValue: bookingDeparture,
			queryValue: queryDeparture,
			lastSearchValue: lastDeparture,
			allowLastSearch: canUseLastSearchForOtherFields,
			isValueValid: value => !isEmpty(value),
		});

		if (departureCity) {
			initialValues.departureCity = departureCity;
		}
	}

	// 2. Dates et durée ne sont reprises que si la destination est connue et la persistance active.
	if (hasDestination && !disableFormPersistance) {
		const departureDate = pickValue({
			bookingValue: normalizeValue(booking?.departureDate),
			lastSearchValue: normalizeValue(lastSearch?.departureDate),
			allowLastSearch: canUseLastSearchForMainFields,
		});

		if (departureDate) {
			initialValues.travelDates.departureDate = departureDate;
		}

		const endDate = pickValue({
			bookingValue: normalizeValue(booking?.endDate),
			lastSearchValue: normalizeValue(lastSearch?.endDate),
			allowLastSearch: canUseLastSearchForMainFields,
		});

		if (endDate) {
			initialValues.travelDates.endDate = endDate;
		}

		const bookingDuration = booking?.duration?.value;
		const lastDuration = lastSearch?.duration?.value;

		const durationCode = pickValue({
			bookingValue: bookingDuration,
			lastSearchValue: lastDuration,
			allowLastSearch: canUseLastSearchForMainFields,
			isValueValid: value => !isNil(value),
		});

		if (!isNil(durationCode)) {
			initialValues.duration = { code: durationCode };
		}
	}

	// 3. Occupancies et cabine ne sont relus que lorsque l'utilisateur a réellement modifié ces champs.
	if (!disableFormPersistance) {
		const bookingOccupancies = hasCustomBookingOccupancies(booking)
			? booking?.occupancies
			: undefined;
		const lastSearchOccupancies =
			lastSearch?.occupancies?.length > 0 ? lastSearch?.occupancies : undefined;

		const occupancies = pickValue({
			bookingValue: bookingOccupancies,
			lastSearchValue: lastSearchOccupancies,
			allowLastSearch: canUseLastSearchForOtherFields,
			isValueValid: value => Array.isArray(value) && value.length > 0,
		});

		if (occupancies) {
			initialValues.occupancies = occupancies;
		}

		const bookingCabin =
			!isEmpty(booking?.cabin) && Boolean(booking?.cabin?.code) ? booking?.cabin : undefined;
		const lastSearchCabin =
			!isEmpty(lastSearch?.cabin) && Boolean(lastSearch?.cabin?.code)
				? lastSearch?.cabin
				: undefined;

		const cabin = pickValue({
			bookingValue: bookingCabin,
			lastSearchValue: lastSearchCabin,
			allowLastSearch: canUseLastSearchForOtherFields,
			isValueValid: value => !isEmpty(value) && Boolean(value?.code),
		});

		if (cabin) {
			initialValues.cabin = cabin;
		}
	}

	return {
		initialValues,
		destinations: state.smartDP.destinations,
		departureCities: state.smartDP.departureCities,
		cabins: state.smartDP.cabins,
		validateOnMount: state.smartDP.status === QUOTATION_CODE_STATUS.BAD_REQUEST,
		topDestinationsList: getTopDestinations(state) || [],
		isMobile: [RESOLUTION.SMALL, RESOLUTION.MEDIUM].includes(state?.resolution),
		brand: state.brand.code,
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			unsetSDPProductsFilter,
			resetAllSDPProductsFilter,
		},
		dispatch
	);
};

export default withRouter(
	injectIntl(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(SmartDPSearchFormContainer)
	)
);
